<template>
  <div class="modal fade show my-2" aria-labelledby="addNewCardTitle" style="display: block; background: #1c1d1c33">
    <div class="modal-dialog modal-md modal-dialog-centered" ref="modalRef">
      <div class="modal-content">
        <div class="row">
          <div class="col-12">
            <div class="d-flex justify-content-between px-1 my-1 align-item-center">
              <h1 class="mb-0">{{ modalTitle }}</h1>
              <button
                  @click="onClickClose"
                  type="button"
                  class="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
              ></button>
            </div>
          </div>
        </div>

        <hr class="mt-0 mb-0">

        <div class="pb-2">
          <div class="row mx-2">
            <div class="col-12">
              <label for="colFormLabel" class="col-form-label">Advance Amount</label>
              <div class="input-group">
                <input
                    v-model="formData.total_paid_amount"
                    type="number"
                    class="form-control"
                    placeholder=""
                    :readonly="! props.isEdit"
                />
              </div>
            </div>

            <div class="col-12">
              <label for="colFormLabel" class="col-form-label">Received In</label>
              <v-select
                  v-model="formData.account_head_id"
                  placeholder="Select Cash & Bank Account"
                  class="w-100"
                  :options="accountHeads"
                  label="name"
                  :reduce="(name) => name.id"
              />
            </div>

            <div class="col-12">
              <label for="colFormLabel" class="col-form-label">Received Ref</label>
              <input
                  type="hidden"
                  class="form-control invoice-edit-input date-picker flatpickr-input"
                  readonly="readonly"
              />
              <div class="input-group input-group-merge invoice-edit-input-group">
                <div class="input-group-text">
                  #
                </div>
                <input
                    v-model="formData.receipt_reference"
                    type="text"
                    class="form-control invoice-edit-input"
                    placeholder=""
                />
              </div>
            </div>

            <div class="col-12">
              <label for="colFormLabel" class="col-form-label">Date</label>
              <div class="input-group">
                <input
                    v-model="formData.receipt_date"
                    type="date"
                    class="form-control"
                    placeholder=""
                />
              </div>
            </div>

            <div class="col-12">
              <label for="colFormLabel" class="col-form-label">Memo</label>
              <textarea
                  v-model="formData.description"
                  placeholder="Memo"
                  class="form-control"
                  rows="2"
                  id="note"
              ></textarea>
            </div>
            <div class="col-12 pt-2">
              <input type="checkbox" id="sendSms" v-model="formData.is_sms" class="form-check-input mx-1">
              <label class="form-check-label" for="sendSms">Send SMS</label>
            </div>

            <div class="col-12 text-center mb-2 mt-2">
              <button :disabled="loading" @click="submit" type="button" class="btn btn-primary full-width me-1 waves-effect waves-float waves-light">
                <div v-if="loading" class="spinner-border spinner-border-sm text-light" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Loader v-if="loader"/>

  </div>
</template>

<script setup>
import Loader from "@/components/atom/LoaderComponent";
import {computed, inject, onMounted, ref, watch} from "vue";
import { useStore } from "vuex";
import { useRoute } from 'vue-router';
import useDate from '@/services/utils/day'
import handleReceipt from '@/services/modules/receipt'
import handleProjects from "@/services/modules/procurement/project";
import handlePurchase from "@/services/modules/purchase";

let formData = ref({
  account_head_id: null,
  account_receivable_id: null,
  total_paid_amount: null,
  receipt_reference: null,
  description: null,
  is_sms: false,
  receipt_date: new Date().toISOString().split('T')[0],
})

const props = defineProps({
  contact: Object,
  generalData: Object,
  isEdit: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'getData']);
const $store = useStore();
const $route = useRoute();
const modalRef = ref(null);
const loading = ref(false);
const loader = ref(false);
const homeContent = ref({});
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { formatDateTime, formatToDate } = useDate()
const projects = ref([]);
const accountHeads = ref([]);
const projectId = ref(null);

const { fetchCashAndBank } = handleReceipt()
const { createInstallmentMoneyReceipt, updateInstallmentMoneyReceipt } = handleProjects()
const { fetchAccountReceivable, fetchMoneyReceipt } = handlePurchase()

const modalTitle = computed(() => {
  if (props.isEdit) return 'Edit Money Receipt'
  return 'Money Receipt'
})

const submit = async() => {
  const query = getQuery();
  loading.value = true;

  if (props.isEdit) {
    await updateReceipt(query)
    return
  }

  await createReceipt(query)
}

function buildInstallmentSetupData() {
  let installmentSetupData = {};
  installmentSetupData.company_id = $route.params.companyId;
  installmentSetupData.contact_profile_id = props.contact.id;
  installmentSetupData.total_paid_amount = parseFloat(formData.value.total_paid_amount);
  installmentSetupData.account_head_id = formData.value.account_head_id;
  installmentSetupData.account_receivable_id = formData.value.account_receivable_id;
  installmentSetupData.receipt_reference = formData.value.receipt_reference;
  installmentSetupData.description = formData.value.description;
  installmentSetupData.receipt_date = formData.value.receipt_date;
  installmentSetupData.is_sms = formData.value.is_sms;
  return installmentSetupData;
}

const createReceipt = async (query) => {
  const installmentSetupData = buildInstallmentSetupData();

  await createInstallmentMoneyReceipt(props.generalData.id, query, installmentSetupData).then((res) => {
    if (! res.status) return showError(res.message)
    emit('getData')
    onClickClose();
    showSuccess(res.message)
  }).catch(() => {
    showError("Something went wrong")
  }).finally(() => {
    loading.value = false
  })
}

const updateReceipt = async (query) => {
  const installmentSetupData = buildInstallmentSetupData();

  await updateInstallmentMoneyReceipt(props.generalData.id, query, installmentSetupData).then((res) => {
    if (! res.status) return showError(res.message)
    emit('getData')
    onClickClose();
    showSuccess(res.message)
  }).catch(() => {
    showError("Something went wrong")
  }).finally(() => {
    loading.value = false
  })
}

function resetForm() {
  formData.value.total_amount = null;
  formData.value.booking_money = null;
  formData.value.down_payment = null;
  formData.value.number_of_installment = null;
  formData.value.installment_frequency = null;
}

const getAccountHead = () => {
  return fetchCashAndBank($route.params.companyId)
      .then(res => {
        if (! res.status) return
        accountHeads.value = res.data
        if (props.isEdit) return
        formData.value.account_head_id = res.data[0].id
      })
}

const getAccountReceivable = () => {
  return fetchAccountReceivable(`?company_id=${$route.params.companyId}`).then(({data}) => {
    if (data.length < 1) {
      return formData.value.account_receivable_id = null;
    }

    formData.value.account_receivable_id = data[0].id
  })
}

const loadExistingReceipt = () => {
  const query = getQuery();
  return fetchMoneyReceipt(props.generalData.receipt_master_id, query).then((res) => {
    if (! res.status) return
    formData.value.account_head_id = res.data.account_head_id
    formData.value.receipt_reference = res.data.receipt_reference
    formData.value.description = res.data.description
    formData.value.receipt_date = formatToDate(res.data.mr_receipt_date)
    formData.value.total_paid_amount = res.data.total_paid_amount
  })
}

onMounted( () => {
  loader.value = true;

  if (! props.isEdit) {
    formData.value.total_paid_amount = props.generalData.amount
  }

  const tasks = [
    getAccountHead(),
    getAccountReceivable(),
  ]

  if (props.isEdit) {
    tasks.push(loadExistingReceipt())
  }

  Promise.all(tasks)
      .catch( () => showError("Something went wrong"))
      .finally( () => loader.value = false)
})

const onClickClose = () => {
  emit('close')
}

function getQuery() {
  return `?company_id=${$route.params.companyId}`;
}

</script>
